import { useState, useEffect } from 'react';

interface UrgencyTimerProps {
  endTime?: Date;
  title?: string;
  description?: string;
}

const UrgencyTimer = ({ 
  endTime = new Date(Date.now() + 24 * 60 * 60 * 1000), // Default: 24 hours from now
  title = "⚡ Flash Sale Ends In:",
  description = "Don't miss out on exclusive deals!"
}: UrgencyTimerProps) => {
  const [timeLeft, setTimeLeft] = useState({
    hours: 0,
    minutes: 0,
    seconds: 0
  });
  const [isClient, setIsClient] = useState(false);

  useEffect(() => {
    setIsClient(true);
  }, []);

  useEffect(() => {
    if (!isClient) return;

    const calculateTimeLeft = () => {
      const difference = endTime.getTime() - Date.now();
      
      if (difference > 0) {
        const newTimeLeft = {
          hours: Math.floor((difference / (1000 * 60 * 60)) % 24),
          minutes: Math.floor((difference / 1000 / 60) % 60),
          seconds: Math.floor((difference / 1000) % 60)
        };
        
        setTimeLeft(prevTime => {
          // Only update if values actually changed to prevent unnecessary renders
          if (prevTime.hours !== newTimeLeft.hours || 
              prevTime.minutes !== newTimeLeft.minutes || 
              prevTime.seconds !== newTimeLeft.seconds) {
            return newTimeLeft;
          }
          return prevTime;
        });
      } else {
        setTimeLeft(prevTime => {
          if (prevTime.hours !== 0 || prevTime.minutes !== 0 || prevTime.seconds !== 0) {
            return { hours: 0, minutes: 0, seconds: 0 };
          }
          return prevTime;
        });
      }
    };

    // Use setTimeout for initial calculation to avoid render phase update
    const initialTimeout = setTimeout(calculateTimeLeft, 0);
    const timer = setInterval(calculateTimeLeft, 1000);

    return () => {
      clearTimeout(initialTimeout);
      clearInterval(timer);
    };
  }, [endTime, isClient]);

  if (!isClient) {
    return (
      <div className="bg-gradient-to-r from-red-600 to-orange-600 text-white py-3 px-6">
        <div className="max-w-7xl mx-auto flex flex-col sm:flex-row items-center justify-center gap-4">
          <div className="flex items-center gap-2">
            <i className="ri-fire-line text-2xl animate-pulse"></i>
            <div>
              <p className="font-bold text-sm">{title}</p>
              <p className="text-xs opacity-90">{description}</p>
            </div>
          </div>
          
          <div className="flex items-center gap-2">
            <div className="bg-white/20 backdrop-blur-sm rounded-lg px-3 py-2 min-w-[60px] text-center">
              <div className="text-2xl font-bold">00</div>
              <div className="text-xs opacity-90">Hours</div>
            </div>
            <span className="text-2xl font-bold">:</span>
            <div className="bg-white/20 backdrop-blur-sm rounded-lg px-3 py-2 min-w-[60px] text-center">
              <div className="text-2xl font-bold">00</div>
              <div className="text-xs opacity-90">Minutes</div>
            </div>
            <span className="text-2xl font-bold">:</span>
            <div className="bg-white/20 backdrop-blur-sm rounded-lg px-3 py-2 min-w-[60px] text-center">
              <div className="text-2xl font-bold">00</div>
              <div className="text-xs opacity-90">Seconds</div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-gradient-to-r from-red-600 to-orange-600 text-white py-3 px-6">
      <div className="max-w-7xl mx-auto flex flex-col sm:flex-row items-center justify-center gap-4">
        <div className="flex items-center gap-2">
          <i className="ri-fire-line text-2xl animate-pulse"></i>
          <div>
            <p className="font-bold text-sm">{title}</p>
            <p className="text-xs opacity-90">{description}</p>
          </div>
        </div>
        
        <div className="flex items-center gap-2">
          <div className="bg-white/20 backdrop-blur-sm rounded-lg px-3 py-2 min-w-[60px] text-center">
            <div className="text-2xl font-bold">{String(timeLeft.hours).padStart(2, '0')}</div>
            <div className="text-xs opacity-90">Hours</div>
          </div>
          <span className="text-2xl font-bold">:</span>
          <div className="bg-white/20 backdrop-blur-sm rounded-lg px-3 py-2 min-w-[60px] text-center">
            <div className="text-2xl font-bold">{String(timeLeft.minutes).padStart(2, '0')}</div>
            <div className="text-xs opacity-90">Minutes</div>
          </div>
          <span className="text-2xl font-bold">:</span>
          <div className="bg-white/20 backdrop-blur-sm rounded-lg px-3 py-2 min-w-[60px] text-center">
            <div className="text-2xl font-bold">{String(timeLeft.seconds).padStart(2, '0')}</div>
            <div className="text-xs opacity-90">Seconds</div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default UrgencyTimer;
